import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import type { ImageLightboxItem } from '../components/ImageLightbox'

type OpenOptions = {
  index?: number
  /** 触发预览的底层资源 publicId（图库用于遮挡标记），可为空 */
  sourcePublicId?: string | null
}

type ImageLightboxContextValue = {
  open: (items: ImageLightboxItem[], options?: OpenOptions) => void
  close: () => void
  isOpen: boolean
  items: ImageLightboxItem[]
  index: number
  sourcePublicId: string | null
  setIndex: (idx: number) => void
}

const ImageLightboxContext = createContext<ImageLightboxContextValue | null>(null)

/**
 * 图片灯箱（预览弹窗）业务态上提到路由之上：
 * 避免切换页面时弹窗 open 状态与内容随页面组件卸载而丢失。
 * 弹窗外壳仍由 components/theme 的 ImageLightbox 承载，符合 web-component-guidelines。
 */
export function ImageLightboxProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ImageLightboxItem[]>([])
  const [index, setIndex] = useState(0)
  const [sourcePublicId, setSourcePublicId] = useState<string | null>(null)
  const [openState, setOpenState] = useState(false)

  const open = useCallback((nextItems: ImageLightboxItem[], options?: OpenOptions) => {
    setItems(nextItems)
    setIndex(options?.index ?? 0)
    setSourcePublicId(options?.sourcePublicId ?? null)
    setOpenState(true)
  }, [])

  const close = useCallback(() => {
    setOpenState(false)
    // 关键：清掉 sourcePublicId——否则 LibraryPage 里的 lightboxOccludedPublicId 还在，
    // 导致 suppressImgNetwork 持续为 true，网格里那张图一直是 skeleton 不会恢复
    setSourcePublicId(null)
  }, [])

  const value = useMemo<ImageLightboxContextValue>(
    () => ({ open, close, isOpen: openState, items, index, sourcePublicId, setIndex }),
    [open, close, openState, items, index, sourcePublicId],
  )

  return <ImageLightboxContext.Provider value={value}>{children}</ImageLightboxContext.Provider>
}

export function useImageLightbox(): ImageLightboxContextValue {
  const ctx = useContext(ImageLightboxContext)
  if (!ctx) {
    throw new Error('useImageLightbox 必须在 ImageLightboxProvider 内使用')
  }
  return ctx
}
